import { Megaphone, MessageCircle, ChevronRight } from 'lucide-react';
import { palette, admin, fontFamily, transition } from '../../../styles/tokens';
import { EmptyState } from '../../../components/common/EmptyState';
import { ConversationSearch } from './ConversationSearch';
import { ConversationRow } from './ConversationRow';
import type { MergedRow, ConversationSummary, Selection } from '../types';

type Filter = 'all' | 'clients' | 'team';

interface Props {
  rows: MergedRow[];
  summaries: Record<string, ConversationSummary>;
  loading: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  filter: Filter;
  onFilterChange: (f: Filter) => void;
  selection: Selection;
  onSelectRow: (kind: 'dm' | 'group', id: string) => void;
  onOpenAnnouncements: () => void;
  announcementPreview: string | null;
}

export function ConversationList({
  rows, summaries, loading, searchQuery, onSearchChange,
  filter, onFilterChange, selection, onSelectRow,
  onOpenAnnouncements, announcementPreview,
}: Props) {
  const announcementsActive = selection?.kind === 'announcements';

  return (
    <div style={{
      width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column',
      borderRight: `1px solid ${admin.border}`, background: palette.white,
      height: '100%', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px', borderBottom: `1px solid ${palette.g100}`,
      }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: palette.g800, fontFamily }}>
          Messages
        </span>
        <button
          onClick={onOpenAnnouncements}
          title="Announcements"
          style={{
            width: 32, height: 32, borderRadius: 8,
            border: `1px solid ${announcementsActive ? palette.accent : admin.border}`,
            background: announcementsActive ? palette.accentWash : palette.white, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: `all ${transition.fast}`,
          }}
        >
          <Megaphone size={16} color={announcementsActive ? palette.accent : palette.g600} strokeWidth={1.8} />
        </button>
      </div>

      <ConversationSearch
        query={searchQuery} onQueryChange={onSearchChange}
        filter={filter} onFilterChange={onFilterChange}
      />

      {/* Pinned Announcements entry */}
      <button
        onClick={onOpenAnnouncements}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          width: '100%', padding: '11px 16px', border: 'none', cursor: 'pointer',
          borderBottom: `1px solid ${palette.g100}`,
          background: announcementsActive ? palette.accentWash : 'transparent',
          fontFamily, textAlign: 'left',
          transition: `background ${transition.fast}`,
        }}
        onMouseEnter={e => { if (!announcementsActive) e.currentTarget.style.background = palette.g50; }}
        onMouseLeave={e => { if (!announcementsActive) e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{
          width: 42, height: 42, borderRadius: 21, flexShrink: 0,
          background: palette.amberWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Megaphone size={19} color={palette.amber} strokeWidth={1.9} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: palette.g800 }}>Announcements</div>
          <div style={{
            fontSize: 12.5, color: palette.g500, marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {announcementPreview ?? 'Broadcast to your team'}
          </div>
        </div>
        <ChevronRight size={16} color={palette.g300} style={{ flexShrink: 0 }} />
      </button>

      {/* Conversation + group list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: palette.g400 }}>
            Loading...
          </div>
        ) : rows.length === 0 ? (
          searchQuery ? (
            <EmptyState
              icon={<MessageCircle size={20} />}
              title="No conversations found"
              subtitle={`Nothing matches "${searchQuery}". Try a different name or filter.`}
            />
          ) : (
            <EmptyState
              icon={<MessageCircle size={20} />}
              title="No conversations yet"
              subtitle="Direct messages and group chats with your team and clients will appear here."
            />
          )
        ) : (
          rows.map(r => (
            <ConversationRow
              key={`${r.kind}-${r.id}`}
              row={r}
              summary={summaries[r.id]}
              isActive={(selection?.kind === r.kind) && 'id' in (selection ?? {}) && (selection as { id: string }).id === r.id}
              onClick={() => onSelectRow(r.kind, r.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
